import { FC, useContext, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";
import { Button } from "../../../components/button/Button";
import { ModalContext } from "../../../components/Modal";
import LoadingOverlay from "../../../components/LoadingOverlay";
import { downloadTrainingVideo } from "../../../redux/adminSlice";
import { AppDispatch } from "../../../redux/store";

export type TrainingVideoDraft = {
  editingVideoId: number | null;
  existingFileName: string;
  videoFile: File | null;
  title: string;
  category: string;
  description: string;
};

type TrainingCategoryOption = {
  value: string;
  label: string;
};

type TrainingVideoModalProps = {
  getInitialDraft: () => TrainingVideoDraft;
  trainingCategoryOptions: readonly TrainingCategoryOption[];
  onCancel: () => void;
  onSave: (draft: TrainingVideoDraft) => Promise<boolean>;
};

const ALLOWED_VIDEO_EXTENSIONS = [".mp4", ".mov"];

const isAllowedVideoFile = (file: File) =>
  ALLOWED_VIDEO_EXTENSIONS.some((extension) =>
    file.name.toLowerCase().endsWith(extension),
  );

type VideoPlayerPopupProps = {
  title: string;
  videoUrl: string;
  isLoading: boolean;
  error: string;
  onClose: () => void;
};

const VideoPlayerPopup = ({
  title,
  videoUrl,
  isLoading,
  error,
  onClose,
}: VideoPlayerPopupProps) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div className="training-video-player-overlay" onMouseDown={onClose}>
      <div
        className="training-video-player-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="training-video-player-close"
          onClick={onClose}
          aria-label="Close video player"
          title="Close"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
        {isLoading ? (
          <div className="training-video-player-state" role="status">
            Loading video...
          </div>
        ) : error ? (
          <div className="training-video-player-state training-field-error">
            {error}
          </div>
        ) : (
          <video
            className="training-video-player"
            src={videoUrl}
            controls
            controlsList="nodownload"
            autoPlay
            playsInline
            aria-label={title}
          />
        )}
      </div>
    </div>
  );
};

const TrainingVideoModal: FC<TrainingVideoModalProps> = ({
  getInitialDraft,
  trainingCategoryOptions,
  onCancel,
  onSave,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const modalContext = useContext(ModalContext);
  const close = modalContext?.close ?? (() => {});
  const [draft, setDraft] = useState<TrainingVideoDraft>(getInitialDraft);
  const initialDraftRef = useRef<TrainingVideoDraft>(draft);
  const [isSaving, setIsSaving] = useState(false);
  const [fileError, setFileError] = useState("");
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const [isLoadingCurrentVideo, setIsLoadingCurrentVideo] = useState(false);
  const [currentVideoError, setCurrentVideoError] = useState("");
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentVideoUrlRef = useRef("");

  const isEditMode = draft.editingVideoId !== null;
  const [isReplacingFile, setIsReplacingFile] = useState(!isEditMode);
  const initialDraft = initialDraftRef.current;
  const hasChanges =
    Boolean(draft.videoFile) ||
    draft.title !== initialDraft.title ||
    draft.category !== initialDraft.category ||
    draft.description !== initialDraft.description;

  useEffect(() => {
    return () => {
      if (currentVideoUrlRef.current) {
        URL.revokeObjectURL(currentVideoUrlRef.current);
      }
    };
  }, []);

  const playCurrentVideo = async () => {
    if (!draft.editingVideoId || isLoadingCurrentVideo) {
      return;
    }

    if (currentVideoUrl) {
      setIsPlayerOpen(true);
      return;
    }

    setCurrentVideoError("");
    setIsPlayerOpen(true);
    setIsLoadingCurrentVideo(true);
    try {
      const videoBlob = await dispatch(
        downloadTrainingVideo(draft.editingVideoId),
      ).unwrap();
      const objectUrl = URL.createObjectURL(videoBlob);
      currentVideoUrlRef.current = objectUrl;
      setCurrentVideoUrl(objectUrl);
    } catch (error) {
      setCurrentVideoError("Video preview is unavailable right now.");
    } finally {
      setIsLoadingCurrentVideo(false);
    }
  };

  const cancelReplaceFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setFileError("");
    setDraft((currentDraft) => ({ ...currentDraft, videoFile: null }));
    setIsReplacingFile(false);
  };

  const handleCancel = () => {
    if (isSaving) {
      return;
    }
    onCancel();
    close();
  };

  const handleSave = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      const didSave = await onSave(draft);
      if (didSave) {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        close();
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="training-video-modal" style={{ position: "relative" }}>
      <div className="training-video-form-card">
        <h4>
          {draft.editingVideoId !== null
            ? "Edit Training Video"
            : "Add Training Video"}
        </h4>
        <div className="training-video-form-grid">
          <div className="training-form-field full-width">
            <label>Video File</label>

            {isEditMode && !isReplacingFile ? (
              <>
                <div className="training-file-row">
                  <button
                    type="button"
                    className="training-file-status"
                    disabled={isSaving || isLoadingCurrentVideo}
                    onClick={playCurrentVideo}
                    aria-label="Play current video"
                  >
                    <span className="training-file-status-icon" aria-hidden="true">
                      &#9654;
                    </span>
                    <span>Current video</span>
                  </button>
                  <button
                    type="button"
                    className="training-file-link"
                    disabled={isSaving}
                    onClick={() => {
                      setFileError("");
                      setIsReplacingFile(true);
                    }}
                  >
                    Replace video
                  </button>
                </div>
                <span className="training-field-hint">
                  This video stays unchanged unless you replace it.
                </span>
                {currentVideoError && (
                  <span className="training-field-hint training-field-error">
                    {currentVideoError}
                  </span>
                )}
              </>
            ) : (
              <>
                <input
                  ref={fileInputRef}
                  id="training-video-file"
                  className="training-file-input"
                  type="file"
                  accept="video/mp4,video/quicktime,.mp4,.mov"
                  disabled={isSaving}
                  onChange={(event) => {
                    const selectedFile = event.target.files?.[0] ?? null;
                    if (selectedFile && !isAllowedVideoFile(selectedFile)) {
                      setFileError("Only .mp4 and .mov formats are allowed.");
                      event.target.value = "";
                      setDraft((currentDraft) => ({
                        ...currentDraft,
                        videoFile: null,
                      }));
                      return;
                    }
                    setFileError("");
                    setDraft((currentDraft) => ({
                      ...currentDraft,
                      videoFile: selectedFile,
                    }));
                  }}
                />
                <div className="training-file-picker-row">
                  <label
                    className="training-file-picker"
                    htmlFor="training-video-file"
                  >
                    Choose video
                  </label>
                  {draft.videoFile && (
                    <span
                      className="training-new-file-name"
                      title={draft.videoFile.name}
                    >
                      Selected: {draft.videoFile.name}
                    </span>
                  )}
                </div>
                {isEditMode && (
                  <div className="training-file-replace-row">
                    <span className="training-field-hint">
                      Select a new file to replace the current video.
                    </span>
                    <button
                      type="button"
                      className="training-file-link"
                      disabled={isSaving}
                      onClick={cancelReplaceFile}
                    >
                      Keep current video
                    </button>
                  </div>
                )}
                {/* {!isEditMode && (
                  <span className="training-field-hint">
                    Supported formats: .mp4, .mov
                  </span>
                )} */}
                {fileError && (
                  <span className="training-field-hint training-field-error">
                    {fileError}
                  </span>
                )}
              </>
            )}
          </div>

          <div className="training-form-field">
            <label>Title</label>
            <input
              type="text"
              value={draft.title}
              disabled={isSaving}
              maxLength={100}
              onChange={(event) =>
                setDraft((currentDraft) => ({
                  ...currentDraft,
                  title: event.target.value,
                }))
              }
              placeholder="Enter training video title"
            />
            <span className="training-field-hint">
              {draft.title.length}/100
            </span>
          </div>

          <div className="training-form-field">
            <label>Category</label>
            <select
              value={draft.category}
              disabled={isSaving}
              onChange={(event) =>
                setDraft((currentDraft) => ({
                  ...currentDraft,
                  category: event.target.value,
                }))
              }
            >
              {!trainingCategoryOptions.some(
                (category) => category.value === draft.category,
              ) && <option value={draft.category}>{draft.category}</option>}
              {trainingCategoryOptions.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          <div className="training-form-field full-width">
            <label>Description</label>
            <textarea
              rows={3}
              value={draft.description}
              disabled={isSaving}
              maxLength={200}
              onChange={(event) =>
                setDraft((currentDraft) => ({
                  ...currentDraft,
                  description: event.target.value,
                }))
              }
              placeholder="Write a short description for users"
            />
            <span className="training-field-hint">
              {draft.description.length}/200
            </span>
          </div>
        </div>

        <div className="training-video-form-actions">
          <Button
            onClick={handleCancel}
            className="btn md"
            color="primary"
            variant="outline"
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="btn md"
            color="primary"
            variant="solid"
            disabled={isSaving || (isEditMode && !hasChanges)}
          >
            {isEditMode ? "Save Changes" : "Save Video"}
          </Button>
        </div>
      </div>
      <LoadingOverlay show={isSaving} />
      {isPlayerOpen && (isLoadingCurrentVideo || currentVideoUrl || currentVideoError) && (
        <VideoPlayerPopup
          title={draft.title || "Training video"}
          videoUrl={currentVideoUrl}
          isLoading={isLoadingCurrentVideo}
          error={currentVideoError}
          onClose={() => setIsPlayerOpen(false)}
        />
      )}
    </div>
  );
};

export default TrainingVideoModal;
